// Sherikning O'Z hisobi: tugun, depozit, topshirilmagan naqd, komissiya.
//
// ⚠️ BU QATLAM `features/cashdesk` DAN MUSTAQIL (07-FRONTEND.md §6:
// `features/*` bir-birini import qilmaydi). Ikkalasi ham agent
// endpointlariga boradi, lekin BOSHQA savol bilan: kassa «hozir qancha
// sarflay olaman» deb so'raydi va javobni har amaldan keyin bekor
// qiladi; bu yerda esa «hisobim qanday turibdi» — rejim, limit va
// komissiya, ya'ni sotuv oqimida umuman kerak bo'lmaydigan maydonlar.
//
// ⚠️ NAQD TOPSHIRISH (`POST /api/agents/{id}/handover`) BU YERDA YO'Q va
// qo'shilmaydi — egasi qarori (11-SHERIK-KABINETI-UI.md §10-1): uni
// XODIM bajaradi. Endpoint `payment.create` huquqi ostida turibdi va u
// `PartnerKind` ning ikkala statik to'plamida ham yo'q, ya'ni qaror
// server tomonda allaqachon majburlangan.

import axios from 'axios'
import { z } from 'zod'
import { api } from '@/shared/api/client'
import {
  debtLimitSchema,
  instantSchema,
  moneySchema,
  toMoney,
  toOptionalMoney,
  uuidSchema,
} from '@/shared/api/schemas'
import { addMoney, negateMoney, type Money } from '@/shared/lib/money'

// --- Sherik tuguni ----------------------------------------------------------

export const PARTNER_STATES = ['ACTIVE', 'SUSPENDED', 'CLOSED'] as const
export type PartnerState = (typeof PARTNER_STATES)[number]

/**
 * Hisob-kitob modeli — `SettlementMode`.
 *
 * ⚠️ IKKI MODEL ARALASHTIRILMAYDI (enum javadoc'i): sherik yo depozitli,
 * yo postpaid. Shuning uchun ekranda ham ular bir ekranning yashiringan
 * qismlari emas, BOSHQA-BOSHQA ko'rinish (§6).
 */
export type SettlementMode = 'DEPOSIT' | 'POSTPAID'

const partnerNodeSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  displayName: z.string().nullish(),
  /**
   * ⚠️ `z.enum(...)` MAJBURIY EMAS: noma'lum qiymat butun sahifani
   * yiqitardi. Noma'lum rejimda ekran ikkala variantdan birini ham
   * TANLAMAYDI — bu farq javobning yarmi (§6), taxmin qilinmaydi.
   */
  settlementMode: z.enum(['DEPOSIT', 'POSTPAID']).nullish(),
  state: z.enum(PARTNER_STATES),
})

export interface PartnerNode {
  id: string
  code: string
  displayName: string
  /** `null` — server bu maydonni bermadi yoki qiymati noma'lum */
  settlementMode: SettlementMode | null
  state: PartnerState
}

/**
 * «Men qaysi sherikman» — `GET /api/agents/{id}`.
 *
 * ⚠️ `id` — `GET /api/me` dagi `partyNodeId`, ya'ni
 * `platform.org_node.id`. Kassa ekranidagi `GET /api/agents/roots`
 * TAKRORLANMADI: u ro'yxat qaytaradi va «qaysi biri men» degan savolni
 * ochiq qoldiradi, bu yerda esa nishon allaqachon ma'lum.
 *
 * ⚠️ Doiradan tashqari id uchun server `404` beradi, `403` emas
 * (`AgentMoneyController.requireVisible` javadoc'i) — ya'ni «topilmadi»
 * bu yerda «sizga tegishli emas» degani ham bo'lishi mumkin.
 */
export async function fetchPartnerNode(agentId: string): Promise<PartnerNode> {
  const response = await api.get<unknown>(`/api/agents/${agentId}`)
  const raw = partnerNodeSchema.parse(response.data)
  return {
    id: raw.id,
    code: raw.code,
    displayName: raw.displayName ?? raw.code,
    settlementMode: raw.settlementMode ?? null,
    state: raw.state,
  }
}

// --- Depozit ----------------------------------------------------------------

const partnerBalanceSchema = z.object({
  balance: moneySchema,
  available: moneySchema,
  /** ⚠️ `Money` YOKI `"NOT_SET"` satri — `AgentBalanceView.debtLimit` */
  debtLimit: debtLimitSchema.nullish(),
})

export interface PartnerBalance {
  /** Depozitda haqiqatan turgani; manfiy bo'lishi mumkin */
  balance: Money
  /** Balans + qarz limiti — hozir sarflash mumkin bo'lgani */
  available: Money
  /**
   * ⚠️ `null` — LIMIT QO'YILMAGAN, NOL EMAS.
   *
   * Farq majburiy (`AgentBalanceView.debtLimit` javadoc'i) va ikki
   * rejimda ikki xil o'qiladi: `DEPOSIT` da «minusga ruxsat yo'q»,
   * `POSTPAID` da «cheklov yo'q». Nol esa ATAYLAB yopilgan limit.
   */
  debtLimit: Money | null
}

export async function fetchPartnerBalance(agentId: string): Promise<PartnerBalance> {
  const response = await api.get<unknown>(`/api/agents/${agentId}/balance`)
  const raw = partnerBalanceSchema.parse(response.data)
  return {
    balance: toMoney(raw.balance),
    available: toMoney(raw.available),
    debtLimit: raw.debtLimit == null ? null : toOptionalMoney(raw.debtLimit),
  }
}

/**
 * Yig'ilgan, hali topshirilmagan naqd — `cash_in_transit` qoldig'i.
 *
 * ⚠️ FAQAT `POSTPAID` DA CHAQIRILADI. `DEPOSIT` rejimida server
 * `SettlementModeMismatch` tashlaydi va bu ataylab: o'sha rejimda naqd
 * umuman jurnalga tushmaydi (`SettlementService` javadoc'i), ya'ni nol
 * ko'rsatish «topshirishim kerakmi?» degan yo'q savolni tug'dirardi.
 */
export async function fetchOutstanding(agentId: string): Promise<Money> {
  const response = await api.get<unknown>(`/api/agents/${agentId}/outstanding`)
  return toMoney(moneySchema.parse(response.data))
}

// --- Komissiya --------------------------------------------------------------

const commissionSummarySchema = z.object({
  from: instantSchema,
  to: instantSchema,
  commission: moneySchema,
  salesCount: z.number(),
  salesAmount: moneySchema,
})

export interface CommissionSummary {
  /** Davr boshi (UTC ISO), kiritiladi */
  from: string
  /** Davr oxiri (UTC ISO), KIRITILMAYDI */
  to: string
  /**
   * CASHBACK — depozitga QAYTGAN summa.
   *
   * ⚠️ Komissiya to'ldirish summasidan yechilmaydi: depozitdan to'liq
   * summa chiqadi, komissiya esa ALOHIDA tranzaksiya bilan (`TxnKind.
   * COMMISSION`) depozitga qaytadi — `02-LEDGER.md` §7.1, 3-qadam.
   * Shuning uchun u {@link salesAmount} dan ayrilgan emas, uning YONIDA
   * turadigan mustaqil son.
   *
   * ⚠️ NOL HAQIQIY JAVOB: `CommissionServiceImpl.accrue` nol komissiyada
   * jurnalga umuman yozuv qo'ymaydi. «Ma'lumot yo'q» deb ko'rsatilmaydi.
   */
  commission: Money
  /** Shu davrdagi abonent to'ldirishlari soni */
  salesCount: number
  /** Depozitdan CHIQQAN to'liq summa (komissiya ayrilmagan) */
  salesAmount: Money
}

/**
 * Sof sarf — depozitdan chiqqan minus qaytgan cashback.
 *
 * ⚠️ SERVERDA BUNDAY MAYDON YO'Q (`CommissionSummary` da uchta pul
 * maydoni bor: `commission` va `salesAmount`). Bu YAGONA hisoblanadigan
 * son va u ikkita SERVER sonidan chiqadi, ya'ni o'ylab topilmagan.
 *
 * ⚠️ Valyutalar mos kelmasa `null` — `addMoney` ataylab ISTISNO tashlaydi
 * va u bu yerda butun sahifani yiqitardi. Ikki valyuta bir davrda
 * uchrashi server nuqsoni bo'lardi, lekin panel undan yiqilmasligi kerak:
 * qolgan ikki son baribir to'g'ri va ko'rsatiladi.
 */
export function netSpend(summary: CommissionSummary): Money | null {
  try {
    return addMoney(summary.salesAmount, negateMoney(summary.commission))
  } catch {
    return null
  }
}

/**
 * `GET /api/agents/{id}/commission-summary` — «bu davrda qancha komissiya
 * ishladim» (egasi qarori, §10-2).
 *
 * ⚠️ SERVER IKKI SONNI AJRATIB BERADI va ular BIR-BIRIGA QO'SHILMAYDI
 * (`CommissionServiceImpl.summary` o'qilgan):
 * <ul>
 *   <li>`salesAmount` — depozitdan CHIQQANI: `TxnKind.TOPUP` ning
 *       manfiy yozuvlari (`POSTPAID` da `cash_in_transit` dan);</li>
 *   <li>`commission` — depozitga QAYTGANI: `TxnKind.COMMISSION` ning
 *       musbat yozuvlari, ya'ni cashback.</li>
 * </ul>
 * Sof sarf — {@link netSpend}. Uchalasini bitta songa yig'ish egasining
 * so'rovini bekor qilardi: u aynan «qancha to'landi, qanchasi cashback
 * bo'lib qaytdi» deb so'ragan (2026-08-19).
 *
 * ⚠️ `REVERSAL` yig'indidan AYIRILMAYDI (server javadoc'i) — bekor
 * qilingan to'ldirish `salesAmount` ichida qoladi.
 *
 * ⚠️ HTTP mapping ENDI BOR (`AgentMoneyController`, `agent.read`), ya'ni
 * bu yerdagi `404` «yozilmagan» degani emas — u `requireVisible` ning
 * «sizga tegishli emas» javobi ham bo'lishi mumkin. {@link
 * isEndpointMissing} ikkalasini bir xil ko'rsatadi, chunki to'g'ri xulq
 * bir xil: son o'ylab topilmaydi.
 *
 * ⚠️ Davr chegarasi berilmasa server JORIY OYNI oladi (tenant zonasida,
 * ADR-015). «Bu oyda qancha ishladim» uchun aynan shu yo'l ishlatiladi —
 * oy chegarasini panelda hisoblash servernikidan farq qilishi mumkin edi.
 */
export async function fetchCommissionSummary(
  agentId: string,
  window?: { from: string; to: string },
): Promise<CommissionSummary> {
  const response = await api.get<unknown>(`/api/agents/${agentId}/commission-summary`, {
    ...(window ? { params: { from: window.from, to: window.to } } : {}),
  })
  const raw = commissionSummarySchema.parse(response.data)
  return {
    from: raw.from,
    to: raw.to,
    commission: toMoney(raw.commission),
    salesCount: raw.salesCount,
    salesAmount: toMoney(raw.salesAmount),
  }
}

/**
 * Xato «endpoint hali yo'q» degani ekanini aniqlaydi.
 *
 * ⚠️ `404` bu yerda IKKI narsani anglatishi mumkin: mapping yozilmagan
 * yoki agent doiradan tashqarida. Ikkalasida ham to'g'ri xulq bir xil —
 * qizil xato emas, «ma'lumot yo'q» deb ochiq aytish. Yolg'on nol
 * ko'rsatishdan ko'ra bo'shlikni tan olish (§7).
 */
export function isEndpointMissing(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 404
}

// --- Kassa amallari ---------------------------------------------------------

/**
 * ⚠️ `kind` YOPIQ RO'YXAT EMAS: server `AgentOperationKind` ga yangi
 * qiymat qo'shsa panel yiqilmasligi kerak. Notanish qiymat matn bo'lib
 * o'tadi va tarjimasi topilmasa kodning o'zi ko'rinadi — bu chalkash,
 * lekin bo'sh ekrandan afzal.
 */
const operationRowSchema = z.object({
  at: instantSchema,
  subscriberId: uuidSchema.nullish(),
  subscriberLogin: z.string().nullish(),
  kind: z.string(),
  amount: moneySchema.nullish(),
  result: z.string(),
  txnId: z.number().nullish(),
  reference: z.string().nullish(),
  /**
   * Server buni `String` qilib beradi (`AgentOperationRow.actor`,
   * o'lchandi 2026-08-19) va qiymat ko'rsatiladigan NOM, `id` emas.
   *
   * ⚠️ Shunday bo'lsa ham `z.string()` EMAS: bitta ustunning shakli
   * o'zgarsa qat'iy sxema BUTUN ro'yxatni yiqitardi va kassir
   * amallarini umuman ko'rmay qolardi. {@link actorLabel} satrni ham,
   * obyektni ham o'qiydi, tanimasa `null` qaytaradi — eng yomon
   * holatda bitta maydon bo'sh qoladi.
   *
   * ⚠️ `null` ODATIY: komissiya va teskari yozuv qatorlarida audit
   * yozuvi yo'q, 2026-08-19 dan oldingi yozuvlarda esa maydonning
   * o'zi yo'q. «SYSTEM» yoki «—» YOZILMAYDI.
   */
  actor: z.unknown(),
  /**
   * Berilgan paketning id'si va KANONIK nomi (server, 2026-08-20).
   *
   * ⚠️ `.nullish()` MAJBURIY, `.nullable()` YETARLI EMAS: backendda
   * `default-property-inclusion: non_null`, ya'ni bo'sh maydon JSON'da
   * `null` bo'lib emas, UMUMAN kelmaydi. `.nullable()` bo'lsa
   * `GRANT` dan boshqa HAR qator sxemadan o'tmasdi va butun ro'yxat
   * yiqilardi.
   *
   * ⚠️ `planName` topilmasa kalit YO'Q, `planId` esa QOLADI — server
   * ataylab «Noma'lum paket» yozmaydi. Ikkalasi ALOHIDA o'qiladi.
   */
  planId: uuidSchema.nullish(),
  planName: z.string().nullish(),
})

const operationPageSchema = z.object({
  rows: z.array(operationRowSchema),
  page: z.number(),
  size: z.number(),
  hasNext: z.boolean(),
})

export interface AgentOperation {
  at: string
  subscriberId: string | null
  subscriberLogin: string | null
  kind: string
  amount: Money | null
  result: string
  txnId: number | null
  reference: string | null
  /**
   * Amalni kim bajargan — depozit ro'yxatining «kim» ustuni.
   *
   * ⚠️ `null` — eski yozuvda aktor SAQLANMAGAN. O'shanda maydon BO'SH
   * qoladi: «SYSTEM» yoki «—» yozish kim qilganini bilamiz degan
   * yolg'on da'vo bo'lardi (egasi qarori, 2026-08-19).
   */
  actor: string | null
  /**
   * Berilgan paketning id'si; `null` — qator paket berish EMAS (yoki
   * yozuv 2026-08-20 dan oldin yozilgan).
   */
  planId: string | null
  /**
   * Serverdagi KANONIK nom — bitta tilda. Ekranda u ZAXIRA: avval
   * mahalliy katalogdan tarjima qilingan nom qidiriladi
   * (`shared/lib/planName.ts` dagi `grantPlanLabel`).
   */
  planName: string | null
}

/**
 * Aktorni ko'rsatiladigan matnga aylantiradi; tanimasa `null`.
 *
 * ⚠️ `JSON.stringify` GA TUSHMAYDI: noma'lum obyekt ekranda
 * `{"id":"3f2a…"}` bo'lib chiqardi va bu kassir uchun matn emas, shovqin.
 */
export function actorLabel(raw: unknown): string | null {
  if (typeof raw === 'string') {
    const trimmed = raw.trim()
    return trimmed === '' ? null : trimmed
  }
  if (raw !== null && typeof raw === 'object') {
    const record = raw as Record<string, unknown>
    for (const field of ['displayName', 'name', 'login', 'username']) {
      const value = record[field]
      if (typeof value === 'string' && value.trim() !== '') return value.trim()
    }
  }
  return null
}

export interface AgentOperationPage {
  rows: AgentOperation[]
  page: number
  size: number
  hasNext: boolean
}

/**
 * `GET /api/agents/{id}/operations` — «bugun nima qildim».
 *
 * ⚠️ Manba IKKITA: ledger ko'chirmasi (summa, `txn`) va audit jurnali
 * (abonent kim, urinish nega o'tmadi). Shuning uchun ikki maydon
 * BO'SH bo'lishi mumkin va bu nosozlik emas:
 * <ul>
 *   <li>`subscriberLogin` — audit yozuvi eski bo'lsa (jurnal
 *       o'zgarmas, unga keyin `login` qo'shib bo'lmaydi);</li>
 *   <li>`txnId` — `FAILED` urinishda tranzaksiya umuman yaratilmagan.</li>
 * </ul>
 *
 * ⚠️ `FAILED` qatorlar audit orqali keladi, u esa outbox → Kafka bilan
 * boradi: rad javobini kassir so'rovning O'ZIDAN darhol oladi, ro'yxatda
 * esa u bir necha soniya kechikib paydo bo'ladi.
 *
 * ⚠️ `moneyOnly` SUKUT BO'YICHA `false` — ESKI xulq (hamma amallar,
 * jumladan paket berishlar). Torayishni CHAQIRUVCHI so'raydi, chunki
 * u ekranning qarori: «Mening amallarim» pul so'raydi, «Berilgan
 * paketlar» esa `kind=GRANT` bilan aynan paketlarni so'raydi.
 */
export async function fetchAgentOperations(
  agentId: string,
  window: { from: string; to: string },
  filter: OperationFilter = EMPTY_FILTER,
  page = 0,
  moneyOnly = false,
  size = OPERATION_PAGE_SIZE,
): Promise<AgentOperationPage> {
  const response = await api.get<unknown>(`/api/agents/${agentId}/operations`, {
    params: operationListParams(window, filter, page, size, moneyOnly),
  })
  return parseOperationPage(response.data)
}

/**
 * Javobni sxemadan o'tkazadi — HTTP'DAN AJRATILGAN.
 *
 * ⚠️ Ajratilgani ataylab: `non_null` sozlamasi tufayli tushib qoladigan
 * maydonlar («kalit UMUMAN yo'q») faqat CHAQIRIB o'lchanadi. Manba
 * matnida `.nullish()` naqshini qidirish bir maydonda `.nullable()`
 * qolib ketganini KO'RMASDI, ro'yxat esa jonli serverda butunlay
 * yiqilardi.
 */
export function parseOperationPage(data: unknown): AgentOperationPage {
  const raw = operationPageSchema.parse(data)
  return {
    rows: raw.rows.map((row) => ({
      at: row.at,
      subscriberId: row.subscriberId ?? null,
      subscriberLogin: row.subscriberLogin ?? null,
      kind: row.kind,
      amount: row.amount ? toMoney(row.amount) : null,
      result: row.result,
      txnId: row.txnId ?? null,
      reference: row.reference ?? null,
      actor: actorLabel(row.actor),
      planId: row.planId ?? null,
      planName: row.planName ?? null,
    })),
    page: raw.page,
    size: raw.size,
    hasNext: raw.hasNext,
  }
}

// --- Amallar filtri (SHARTNOMA §6) ------------------------------------------

/**
 * ⚠️ Bu ro'yxat FILTR TANLAGICHI uchun, javobni o'qish uchun EMAS.
 * Server `OTHER` ni ham qaytaradi va u qatorlarda ko'rinadi
 * ({@link operationRowSchema} dagi `kind: z.string()`), lekin tanlash
 * uchun taklif qilinmaydi: «boshqa» ni tanlash kassirga hech narsa
 * aytmaydi.
 */
export const OPERATION_KINDS = [
  'FUND',
  'DEPOSIT',
  'COMMISSION',
  'HANDOVER',
  'GRANT',
  'REVERSAL',
] as const
export type OperationKind = (typeof OPERATION_KINDS)[number]

/**
 * Tur TANLAGICHIDA taklif qilinadigan ro'yxat — {@link OPERATION_KINDS}
 * dan TOR.
 *
 * ⚠️ `GRANT` bu yerda YO'Q. Egasi qarori, 2026-08-20: «tranzaksiyani
 * ko'ryapti va filtrda paket bo'yicha kerak emas». Paket berishlar O'Z
 * bo'limiga chiqarildi («Berilgan paketlar»), ya'ni bu tanlov endi
 * ikkinchi, chalkash yo'l bo'lardi.
 *
 * ⚠️ {@link OPERATION_KINDS} DAN O'CHIRILMADI va o'chirilmaydi ham: u
 * yerdan qatorlarning BEZAGI o'qiladi (`myOperations.kind.GRANT`) va
 * o'sha ro'yxatdan chiqarilsa paket qatorlari ekranda xom kod bilan
 * chiqardi. Ikki ro'yxatning FARQI shu bitta qiymatda va uni
 * `tests/guards.mjs` §13 qo'riqlaydi.
 */
export const OPERATION_FILTER_KINDS = [
  'FUND',
  'DEPOSIT',
  'COMMISSION',
  'HANDOVER',
  'REVERSAL',
] as const satisfies readonly OperationKind[]

/**
 * ⚠️ `PARTIAL` FILTRDA YO'Q va bu SHARTNOMA §6 qarori: server faqat
 * `OK|FAILED` ni qabul qiladi. Qatorda esa u uchraydi va o'z tegi bilan
 * chiziladi — tanlagichdagi ro'yxat javobdagi to'plam bilan bir xil
 * bo'lishi shart emas.
 */
export const OPERATION_FILTER_STATUSES = ['OK', 'FAILED'] as const
export type OperationFilterStatus = (typeof OPERATION_FILTER_STATUSES)[number]

/** SHARTNOMA §6: uzunroq matnda server 400 qaytaradi */
export const OPERATION_QUERY_MAX = 64

/** Bitta sahifadagi qatorlar — ro'yxat SERVERDA sahifalanadi */
export const OPERATION_PAGE_SIZE = 50

export interface OperationFilter {
  /** Bo'sh ro'yxat = HAMMASI (§6), «hech biri» EMAS */
  kinds: readonly OperationKind[]
  /** `null` = hammasi */
  status: OperationFilterStatus | null
  /** Tranzaksiya raqami, havola yoki abonent logini; bo'sh = filtrlanmaydi */
  q: string
}

export const EMPTY_FILTER: OperationFilter = { kinds: [], status: null, q: '' }

/**
 * «Berilgan paketlar» bo'limi so'raydigan yagona tur.
 *
 * ⚠️ Ayni so'rov, ayni endpoint (`GET /api/agents/{id}/operations`) —
 * ikkinchi endpoint OCHILMADI: javob AYNI ro'yxatda allaqachon bor va
 * ikkinchi manba ikkinchi kesh, ikkinchi bo'sh holat degani bo'lardi.
 * Ajratilgani — EKRAN, so'rov emas (egasi talabi: «alohida menyu»).
 */
export const GRANT_FILTER: OperationFilter = { kinds: ['GRANT'], status: null, q: '' }

export function isFilterActive(filter: OperationFilter): boolean {
  return filter.kinds.length > 0 || filter.status !== null || filter.q.trim() !== ''
}

/**
 * «Paketsiz» so'rovining SIM SHAKLI — YAGONA JOY.
 *
 * ⚠️ Backendda shakl HALI TANLANMAGAN (SHARTNOMA-3 §A2:
 * `excludeKinds=GRANT` yoki `moneyOnly=true`). Shakl aniq bo'lganda
 * FAQAT SHU JUFTLIK o'zgaradi — sxema, kesh kaliti va ekranlar undan
 * mustaqil. Nom boshqa hech qayerda satr bo'lib takrorlanmaydi va buni
 * `tests/money-only.mjs` sanab qo'riqlaydi: ikkinchi nusxa paydo
 * bo'lsa, «bitta qator» va'dasi jimgina buzilardi.
 */
const MONEY_ONLY_PARAM: readonly [name: string, value: string] = ['moneyOnly', 'true']

/**
 * Torayish HAQIQATDA qo'llanadimi.
 *
 * ⚠️ ANIQ TUR TANLANGANDA — YO'Q. Server ham shu qoidani va'da qiladi
 * («aniq so'ralgan bayroqdan ustun», §A2), lekin panel unga TAYANMAYDI:
 * `kind=GRANT` bilan bayroqni ham yuborish, server ustunlikni hali
 * yozib ulgurmagan bo'lsa, «Berilgan paketlar» ni jimgina BO'SH
 * qoldirardi — va bo'shligi «paket bermaganman» bo'lib o'qilardi.
 *
 * ⚠️ Kesh kaliti ham AYNAN shu funksiyadan o'qiladi (`profileKeys`):
 * aks holda bir xil so'rov ikki kalitga bo'linib, ikki marta ketardi.
 */
export function moneyOnlyApplies(filter: OperationFilter, moneyOnly: boolean): boolean {
  return moneyOnly && filter.kinds.length === 0
}

/**
 * So'rov parametrlari — `URLSearchParams`, axios obyektidan EMAS.
 *
 * ⚠️ SABAB O'LCHANADIGAN: axios massivni sukut bo'yicha `kind[]=FUND`
 * deb yozadi, SHARTNOMA §6 esa TAKRORLANUVCHI `kind=FUND&kind=DEPOSIT`
 * ni talab qiladi. Spring `kind[]` nomini umuman tanimaydi — filtr
 * JIMGINA e'tiborsiz qolardi va ekranda filtrlanmagan ro'yxat
 * «filtr topmadi» degan taassurot bilan turardi.
 *
 * ⚠️ TORAYISH SHU YERDA, MIJOZDA KESISH TAQIQ (SHARTNOMA-3 §D):
 * ro'yxat SERVERDA sahifalanadi, ya'ni qatorlarni kelgandan keyin
 * elash faqat JORIY sahifani toraytirardi — «Yana yuklash» esa o'sha
 * paketlarni qayta olib kelaverardi.
 */
export function operationListParams(
  window: { from: string; to: string },
  filter: OperationFilter,
  page: number,
  size: number,
  moneyOnly = false,
): URLSearchParams {
  const params = new URLSearchParams()
  params.set('from', window.from)
  params.set('to', window.to)
  for (const kind of filter.kinds) params.append('kind', kind)
  if (moneyOnlyApplies(filter, moneyOnly)) params.append(...MONEY_ONLY_PARAM)
  if (filter.status !== null) params.set('status', filter.status)
  const q = filter.q.trim()
  // ⚠️ QIRQILMAYDI: qirqilgan matn BOSHQA so'rov bo'lardi va natija
  // «topilmadi» emas, YOLG'ON topilma bo'lib chiqardi. Uzunlikni
  // maydonning o'zi to'sadi (`maxLength`), oshib ketsa — server 400.
  if (q !== '') params.set('q', q)
  params.set('page', String(page))
  params.set('size', String(size))
  return params
}

/**
 * Shu to'ldirish bo'yicha qaytgan cashback — RO'YXATNING O'ZIDAN.
 *
 * ⚠️ QATORDA KOMISSIYA MAYDONI YO'Q va qo'shilmaydi ham:
 * `AgentOperationRow` da bunday maydon umuman yo'q, chunki cashback —
 * ALOHIDA tranzaksiya (`02-LEDGER.md` §7.1, 3-qadam) va ro'yxatda ham
 * alohida qator bo'lib turadi (`kind === 'COMMISSION'`, musbat summa).
 *
 * ⚠️ BOG'LOVCHI — `reference`, ya'ni idempotentlik kaliti. Ikkala
 * tranzaksiya ham AYNAN bir xil qiymatni yozadi: to'ldirish
 * `DepositServiceImpl.fundSubscriber` da `.reference(idempotencyKey)`,
 * komissiya esa `CommissionServiceImpl.accrue` da o'sha kalit bilan
 * (o'zining txn kaliti `agent:commission:<agentId>:<key>` boshqa, lekin
 * `reference` bir xil).
 *
 * ⚠️ `null` IKKI narsani anglatadi va ular AJRATILMAYDI: cashback yo'q,
 * yoki uning qatori shu SAHIFAGA tushmagan (ro'yxat sahifalanadi va
 * komissiya jurnalga bir necha soniya kechikib tushadi). Shuning uchun
 * ekranda `0` deb emas, chiziqcha bilan ko'rsatiladi — bu yerda nol
 * yozish «komissiya berilmadi» degan tekshirilmagan da'vo bo'lardi.
 * Davr bo'yicha ISHONCHLI son `commission-summary` da.
 */
export function cashbackOf(rows: readonly AgentOperation[], row: AgentOperation): Money | null {
  if (row.kind !== 'FUND' || row.reference === null) return null
  const hit = rows.find(
    (other) => other.kind === 'COMMISSION' && other.reference === row.reference,
  )
  return hit?.amount ?? null
}

// --- Kunlik va oylik pul chegarasi ------------------------------------------

/**
 * ⚠️ `LimitKind` BILAN CHALKASHTIRILMAYDI — backend ularni ataylab ikki
 * endpointda saqlaydi (`FundLimitKind` javadoc'i): `LimitKind` AMAL SONI
 * (dona), bu esa PUL (minor birlik). Ikkalasini bir ustunga qo'yish
 * «dona» va «so'm» ni aralashtirardi.
 */
export const FUND_LIMIT_KINDS = ['DAILY', 'MONTHLY', 'PER_TXN'] as const
export type FundLimitKind = (typeof FUND_LIMIT_KINDS)[number]

/**
 * OYNA limitlari — «sarflandi / qoldi» ma'noga EGA bo'lganlari.
 *
 * ⚠️ `PER_TXN` BU RO'YXATDA YO'Q va bu tip darajasidagi to'siq, uslub
 * emas: u oyna limiti emas, BITTA amalning shifti. Unda «sarflandi»
 * degan hisoblagich umuman yo'q, ya'ni «yana qancha qoldi» degan son
 * O'YLAB TOPILGAN bo'lardi — kunlik chegaradek ko'rinib, kun davomida
 * kamaymasdi. {@link remainingOf} shuning uchun uni argument sifatida
 * ham qabul qilmaydi.
 */
export const FUND_WINDOW_KINDS = ['DAILY', 'MONTHLY'] as const
export type FundWindowKind = (typeof FUND_WINDOW_KINDS)[number]

/**
 * ⚠️ VALYUTA YO'Q — server `Map<FundLimitKind, Long>` beradi, ya'ni
 * yalang'och MINOR son. `Money` ga aylantirish chaqiruvchida bo'ladi va
 * valyuta `GET /balance` dan olinadi; usiz son ko'rsatilmaydi.
 */
const fundLimitsSchema = z.object({
  limits: z.record(z.string(), z.number()).nullish(),
  used: z.record(z.string(), z.number()).nullish(),
})

export interface FundLimits {
  /** MINOR birlikda; `null` — CHEKSIZ (`0` esa umuman ruxsat yo'q) */
  limits: Record<FundLimitKind, number | null>
  /** JORIY oynada sarflangani, MINOR birlikda */
  used: Record<FundLimitKind, number>
}

/**
 * `GET /api/agents/{id}/fund-limits` — «yana qancha qoldi».
 *
 * ⚠️ Kalit XARITADA BO'LMASLIGI ham «cheksiz» degani: `FundLimits`
 * konstruktori `null` qiymatlarni xaritaga umuman qo'ymaydi. Shuning
 * uchun ikkala tur ham har doim o'qiladi, yo'g'i `null` ga tushadi.
 *
 * ⚠️ `used` — «joriy oynada», «oy boshidan» EMAS: davr belgisi eskirgan
 * bo'lsa server `0` beradi (`FundLimitRepository.load`). Va u FAQAT
 * abonent to'ldirishida o'sadi — depozit va komissiya uni yemaydi.
 */
export async function fetchFundLimits(agentId: string): Promise<FundLimits> {
  const response = await api.get<unknown>(`/api/agents/${agentId}/fund-limits`)
  const raw = fundLimitsSchema.parse(response.data)
  const limits = {} as Record<FundLimitKind, number | null>
  const used = {} as Record<FundLimitKind, number>
  for (const kind of FUND_LIMIT_KINDS) {
    limits[kind] = raw.limits?.[kind] ?? null
    used[kind] = raw.used?.[kind] ?? 0
  }
  return { limits, used }
}

/**
 * Qolgan miqdor; cheksiz chegarada `null`.
 *
 * ⚠️ SERVER BU SONNI BERMAYDI. `FundLimits.remainingOf` — argumentli
 * METOD, ya'ni Jackson uni JSON'ga umuman chiqarmaydi. Formula
 * serverdagining aynan o'zi: `max(0, limit - used)` — `used` limitdan
 * oshib ketgan holatda manfiy son ko'rsatish «qarzdorman» degan yo'q
 * ma'noni berardi.
 */
export function remainingOf(value: FundLimits, kind: FundWindowKind): number | null {
  const limit = value.limits[kind]
  return limit === null ? null : Math.max(0, limit - value.used[kind])
}

/**
 * Bitta to'lovning shifti, MINOR birlikda; `null` — chegara yo'q.
 *
 * ⚠️ SUKUT QIYMAT KODDA YO'Q. Server uni `100 000 AFN` deb belgilaydi,
 * lekin uni bu yerga yozib qo'yish tenant chegarani o'zgartirgan kuni
 * ekranda ESKI sonni qoldirardi — va u ishonarli ko'rinardi. Chegara
 * kelmasa «cheklov yo'q» deyiladi, chunki javob aynan shuni aytdi.
 */
export function perTxnLimit(value: FundLimits): number | null {
  return value.limits.PER_TXN
}
